import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PRODUCTS } from "@/lib/crm";
import { SlotPicker } from "./SlotPicker";
import { TypePicker } from "./TypePicker";

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>
);

export const BookingForm = ({ slug, info, onBooked }) => {
  const [slot, setSlot] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", product: "mutuo", amount: "", duration_months: "", purpose: "", job: "", income: "", city: "", notes: "", type: info.types[0], privacy_consent: false });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const submit = async () => {
    if (!slot) return toast.error("Scegli un orario");
    if (!f.first_name || !f.last_name || !f.phone) return toast.error("Inserisci nome, cognome e telefono");
    if (!f.privacy_consent) return toast.error("È necessario il consenso privacy");
    setSaving(true);
    try {
      const num = (v) => (v ? Number(v) : null);
      const r = await api.post(`/public/booking/${slug}/book`, { ...f, amount: num(f.amount), duration_months: num(f.duration_months), income: num(f.income), start: slot });
      onBooked(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="booking-form">
      <section className="space-y-3">
        <h3 className="font-display font-semibold">1. Scegli giorno e orario</h3>
        <SlotPicker url={`/public/booking/${slug}/slots`} info={info} value={slot} onChange={setSlot} />
      </section>
      <section className="space-y-3">
        <h3 className="font-display font-semibold">2. Come preferisci incontrarci?</h3>
        <TypePicker types={info.types} value={f.type} onChange={set("type")} />
        {f.type === "ufficio" && info.office_address && <p className="text-xs text-slate-500">Indirizzo: {info.office_address}</p>}
      </section>
      <section className="space-y-4">
        <h3 className="font-display font-semibold">3. I tuoi dati</h3>
        <div className="flex flex-wrap gap-2">
          {PRODUCTS.map((p) => (
            <button key={p.id} type="button" data-testid={`book-product-${p.id}`} onClick={() => setF({ ...f, product: p.id })}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${f.product === p.id ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200 hover:border-slate-400"}`}>{p.label}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome *"><Input data-testid="book-first-name" value={f.first_name} onChange={set("first_name")} /></Field>
          <Field label="Cognome *"><Input data-testid="book-last-name" value={f.last_name} onChange={set("last_name")} /></Field>
          <Field label="Telefono *"><Input data-testid="book-phone" type="tel" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Email (per la conferma)"><Input data-testid="book-email" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label="Città"><Input data-testid="book-city" value={f.city} onChange={set("city")} /></Field>
          <Field label="Importo indicativo (€)"><Input data-testid="book-amount" type="number" value={f.amount} onChange={set("amount")} /></Field>
          <Field label="Durata desiderata (mesi)"><Input data-testid="book-duration" type="number" value={f.duration_months} onChange={set("duration_months")} placeholder="es. 120" /></Field>
          <Field label="Finalità"><Input data-testid="book-purpose" value={f.purpose} onChange={set("purpose")} placeholder="es. acquisto prima casa, liquidità" /></Field>
          <Field label="Professione (facoltativo)"><Input data-testid="book-job" value={f.job} onChange={set("job")} placeholder="es. dipendente pubblico, pensionato" /></Field>
          <Field label="Reddito netto mensile (€, facoltativo)"><Input data-testid="book-income" type="number" value={f.income} onChange={set("income")} /></Field>
        </div>
        <Field label="Note (facoltativo)"><Textarea data-testid="book-notes" rows={2} value={f.notes} onChange={set("notes")} placeholder="Es. vorrei rinegoziare il mutuo attuale" /></Field>
        <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer">
          <Checkbox data-testid="book-privacy" checked={f.privacy_consent} onCheckedChange={(v) => setF({ ...f, privacy_consent: !!v })} className="mt-0.5" />
          Acconsento al trattamento dei miei dati personali per essere ricontattato in merito alla mia richiesta (GDPR UE 2016/679). <a data-testid="book-privacy-link" href={`/prenota/${slug}/privacy`} target="_blank" rel="noreferrer" className="text-sky-700 underline">Leggi l'informativa</a>
        </label>
      </section>
      <Button data-testid="book-submit-btn" onClick={submit} disabled={saving} className="w-full h-12 rounded-full bg-[#0F172A] hover:bg-sky-600 text-base">
        {saving ? "Prenotazione…" : "Conferma appuntamento"}
      </Button>
    </div>
  );
};
